import { Text, useTheme } from '@chakra-ui/react';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { ColumnHelper, createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useMemo } from 'react';
import { PlatformInstagramColorIcon } from 'ui-components/dist/PlatformInstagramColorIcon';
import { PlatformSnapchatColorIcon } from 'ui-components/dist/PlatformSnapchatColorIcon';
import { PlatformTikTokColorIcon } from 'ui-components/dist/PlatformTikTokColorIcon';
import { PlatformYouTubeColorIcon } from 'ui-components/dist/PlatformYouTubeColorIcon';
import { sortDirections, sortFields } from '~/clientApi/agencies.model';
import { IClientApiResponse } from '~/clientApi/clientApi.types';
import { Creator } from '~/clientApi/creator.model';
import Spinner from '~/components/Spinner';
import { Table } from '~/components/Table/Table';
import { TalentRosterEmptyResults } from '~/components/TalentRosterEmptyResults';
import { Gender } from '~/components/TalentRosterGenderTag';
import { getCreatorLocation } from '~/core/utils';
import { useListsStore } from '~/screens/WhalarModeListDetailScreen/store/useSavedListStore';
import {
  AgencyData,
  ColumnHeaderIds,
  ColumnSocialPlatform,
  columnHeaderIdToHeapNameMap,
  customHeaderIds,
  getColumnDefinitions,
} from '~/screens/WhalarModeScreen/components/ListViewRoster';
import { AgeCell } from '~/screens/WhalarModeScreen/components/Table/AgeCell';
import { BioCell } from '~/screens/WhalarModeScreen/components/Table/BioCell';
import { ClientNameCell } from '~/screens/WhalarModeScreen/components/Table/ClientNameCell';
import { TextInfo } from '~/screens/WhalarModeScreen/components/Table/Columns';
import { HeaderCell } from '~/screens/WhalarModeScreen/components/Table/HeaderCell';
import {
  SkeletonAvatarCell,
  SkeletonCell,
} from '~/screens/WhalarModeScreen/components/Table/SkeletonComponents';
import { Verticals } from '~/screens/WhalarModeScreen/components/Table/Verticals';
import { tdProps } from '~/screens/WhalarModeScreen/components/Table/styles';
import { useTableSkeleton } from '~/screens/WhalarModeScreen/hooks/useTableSkeleton';
import {
  useAdvancedFiltersStore,
  useMarketerSearchTalentStore,
  useTalentFiltersStore,
} from '~/screens/store';
import { SearchCreatorsApiResponse, allowedSortFields } from '~/searchApi';
import { ICreator } from '~/types';

import { InviteCreatorEmptyPage } from '../StackView/InviteCreatorEmptyPage';
import { Platform } from '../Table/Platform';
import { RowCell } from '../Table/RowCell';
import { WebLinksCell } from '../Table/WebLinksCell';

interface ListViewProps {
  defaultSortedColumns?: {
    name: boolean;
    age: boolean;
    location: boolean;
    talentManager: boolean;
  };
  toggleOrder: (field: allowedSortFields) => void;
  isFieldSorted: (field: string) => false | sortDirections;
  marketerData: ICreator[];
  error: unknown;
  hasNextPage?: boolean;
  isInitialFetch: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isSelectorActive?: boolean;
  isPlatformsSortable?: boolean;
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<
    InfiniteQueryObserverResult<
      IClientApiResponse<Creator> | SearchCreatorsApiResponse | undefined,
      unknown
    >
  >;
  isShared: boolean;
}

// This component is almost identical to the ListView component in src/screens/CreatorMarketer/components/ListView/ListView.tsx
// we decouple this one from the original to make it easier to remove the ListView component in the future

export const ListViewRoster: React.FC<ListViewProps> = ({
  defaultSortedColumns = {
    age: true,
    name: true,
    talentManager: true,
  },
  toggleOrder,
  isFieldSorted,
  marketerData,
  error,
  hasNextPage,
  isInitialFetch,
  isFetchingNextPage,
  fetchNextPage,
  isLoading,
  isSelectorActive = true,
  isPlatformsSortable = true,
  isShared,
}) => {
  const theme = useTheme();
  const { appliedFilters: advancedFilters } = useAdvancedFiltersStore();
  const { appliedFilters: creatorsFilters } = useTalentFiltersStore();

  const appliedFilters = [...advancedFilters, ...creatorsFilters];

  const { creators: storedCreators } = useListsStore();
  const { query } = useMarketerSearchTalentStore();

  const isRowSelected = useCallback(
    (creatorId: string) => !!storedCreators.find((c) => c.id === creatorId),
    [storedCreators],
  );

  const getSelectedRowStyles = useCallback(
    (creator: ICreator) => {
      if (isRowSelected(creator.id)) {
        return {
          backgroundColor: '#F6F0EB',
          borderBottomColor: 'secondary.500',
          borderBottomWidth: 1,
        };
      }

      return {};
    },
    [isRowSelected],
  );

  const genericSort = useCallback(
    (field: sortFields) => {
      toggleOrder(field);
      // e.stopPropagation();
    },
    [toggleOrder],
  );

  const columnHelper = createColumnHelper<ICreator>();

  const columnDefiners: Partial<
    Record<keyof AgencyData, Parameters<ColumnHelper<any>['accessor']>[1]>
  > = {
    [ColumnHeaderIds.name]: {
      id: 'name', // will be used as the key for generic sort function
      enableSorting: defaultSortedColumns.name,
      cell: ({ row }) => (
        <RowCell>
          <ClientNameCell
            row={row}
            isShared={isShared}
            isSelectorActive={isSelectorActive}
          />
        </RowCell>
      ),
      header: (header) => (
        <HeaderCell
          text="Name"
          isSortable={defaultSortedColumns.name}
          isFetching={isLoading}
          justifyContent="left"
          isFieldSorted={isFieldSorted}
          header={header}
          creators={tableData}
        />
      ),
    },
    [ColumnHeaderIds.bioAgency]: {
      id: 'bio',
      enableSorting: false,
      header: (header) => (
        <HeaderCell
          text="BIO"
          justifyContent="left"
          maxWidth="300"
          header={header}
          width={{
            base: 'auto',
            md: '300px',
          }}
        />
      ),
      cell: ({ row }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell>
            <BioCell row={row} />
          </RowCell>
        ),
    },
    [ColumnHeaderIds.categoryAgency]: {
      header: (header) => (
        <HeaderCell text="VERTICALS" justifyContent="left" header={header} />
      ),
      enableSorting: false,
      cell: ({ row }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell>
            <Verticals row={row} />
          </RowCell>
        ),
    },
    [ColumnHeaderIds.age]: {
      header: (header) => (
        <HeaderCell
          text="Age"
          isSortable={defaultSortedColumns.age}
          isFetching={isLoading}
          justifyContent="left"
          isFieldSorted={isFieldSorted}
          header={header}
        />
      ),
      cell: ({ row }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell>
            <AgeCell row={row} />
          </RowCell>
        ),
      enableSorting: defaultSortedColumns.age,
    },
    [ColumnHeaderIds.genderAgency]: {
      enableSorting: false,
      header: (header) => (
        <HeaderCell text="GENDER" justifyContent="center" header={header} />
      ),
      cell: ({ row }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell>
            <Gender row={row} />
          </RowCell>
        ),
    },
    [ColumnHeaderIds.location]: {
      header: (header) => (
        <HeaderCell
          text="LOCATION"
          isFetching={isLoading}
          justifyContent="left"
          isFieldSorted={isFieldSorted}
          header={header}
        />
      ),
      cell: ({ row }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell>
            <TextInfo text={getCreatorLocation(row.original) ?? ''} />
          </RowCell>
        ),
      enableSorting: false,
    },
    [ColumnHeaderIds.instagram]: {
      id: ColumnSocialPlatform.instagram, // this id is being used to sort instagram platform column
      enableSorting: isPlatformsSortable,
      header: (header) => (
        <HeaderCell
          justifyContent="left"
          isSortable={isPlatformsSortable}
          header={header}
          minWidth={10}
          isFieldSorted={isFieldSorted}
          textIcon={<PlatformInstagramColorIcon boxSize={4} />}
        />
      ),
      cell: ({ getValue }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell backgroundColor="neutral.100">
            <Platform platform={getValue()} socialPlatform="IG" />
          </RowCell>
        ),
    },
    [ColumnHeaderIds.tiktok]: {
      id: ColumnSocialPlatform.tiktok, // this id is being used to sort tiktok platform column
      enableSorting: isPlatformsSortable,
      header: (header) => (
        <HeaderCell
          justifyContent="left"
          header={header}
          minWidth={10}
          isSortable={isPlatformsSortable}
          isFieldSorted={isFieldSorted}
          textIcon={<PlatformTikTokColorIcon boxSize={4} />}
        />
      ),
      cell: ({ getValue }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell>
            <Platform platform={getValue()} socialPlatform="TT" />
          </RowCell>
        ),
    },
    [ColumnHeaderIds.youtube]: {
      id: ColumnSocialPlatform.youtube, // this id is being used to sort youtube platform column
      enableSorting: isPlatformsSortable,
      header: (header) => (
        <HeaderCell
          justifyContent="left"
          header={header}
          minWidth={10}
          isSortable={isPlatformsSortable}
          isFieldSorted={isFieldSorted}
          textIcon={<PlatformYouTubeColorIcon boxSize={4} />}
        />
      ),
      cell: ({ getValue }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell backgroundColor={'neutral.100'}>
            <Platform platform={getValue()} socialPlatform="YT" />
          </RowCell>
        ),
    },
    [ColumnHeaderIds.snapchat]: {
      id: ColumnSocialPlatform.snapchat, // this id is being used to sort snapchat platform column
      enableSorting: isPlatformsSortable,
      header: (header) => (
        <HeaderCell
          justifyContent="left"
          header={header}
          minWidth={10}
          isSortable={isPlatformsSortable}
          isFieldSorted={isFieldSorted}
          textIcon={<PlatformSnapchatColorIcon boxSize={4} />}
        />
      ),
      cell: ({ getValue }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell>
            <Platform platform={getValue()} socialPlatform="SC" />
          </RowCell>
        ),
    },
    [ColumnHeaderIds.webLinks]: {
      header: (header) => (
        <HeaderCell text="LINKS" justifyContent="left" header={header} />
      ),
      enableSorting: false,
      cell: ({ row }) =>
        isLoading ? (
          <SkeletonCell />
        ) : (
          <RowCell>
            <WebLinksCell row={row} />
          </RowCell>
        ),
    },
  };

  const { tableColumnDefiners, tableData } = useTableSkeleton({
    columnDefiners,
    data: marketerData,
    isLoading: isLoading,
    numberOfRows: 10,
    skeletonMapper: { name: <SkeletonAvatarCell /> },
    defaultSkeleton: <SkeletonCell />,
  });

  const columnDefinition = getColumnDefinitions(
    columnHelper,
    tableColumnDefiners,
  );

  const tableTrStyle = useMemo(() => {
    const commonStyle = {
      _active: {
        '& > td': {
          backgroundColor: theme.colors.neutral[200],
        },
      },
    };
    return isShared
      ? commonStyle
      : {
          ...commonStyle,
          _active: {
            '& > td': {
              backgroundColor: '#FFFAF5',
            },
          },
        };
  }, [isShared, theme.colors.neutral]);

  if (isInitialFetch) {
    return <Spinner centered />;
  }

  if (error) {
    return <Text color="error.500">Something went wrong.</Text>;
  }

  if (
    (appliedFilters.length && !tableData.length) ||
    (!marketerData.length && query.length)
  ) {
    return <TalentRosterEmptyResults />;
  }

  if (!tableData.length) {
    return <InviteCreatorEmptyPage />;
  }

  const handleEndReached = () => {
    if (hasNextPage === true && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  };

  return (
    <Table
      columns={columnDefinition}
      data={tableData}
      genericSort={genericSort}
      getSelectedRowStyles={getSelectedRowStyles}
      tdProps={tdProps}
      customHeaderIds={customHeaderIds}
      columnHeaderIdToHeapNameMap={columnHeaderIdToHeapNameMap}
      onEndReached={() => handleEndReached()}
      tableRowStyle={tableTrStyle}
    />
  );
};
